import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Tag as AntdTag, Typography } from 'antd';
import cn from 'classnames';
import throttle from 'lodash/throttle';
import classes from './Tag.module.scss';
import { Tooltip } from '../index';

const TooltipProps = {
  placement: 'bottomLeft',
};

const { Paragraph } = Typography;

export const Tag = (props) => {
  const { className, asChild, ...tagProps } = props;
  if (asChild) {
    return <>{tagProps.children}</>;
  }
  return <AntdTag className={cn(classes.tag, className)} {...tagProps} />;
};

export const TagGroup = ({
  tooltipProps: tooltipFromProps,
  tags = [],
  maxCount,
  renderTooltipContent: renderTooltipContentFromProps,
  direction = 'horizontal',
  wrap = false,
  asMuchAsPossible = false,
  itemWidth: itemWidthFromProps = 50,
}) => {
  const tooltipProps = {
    ...TooltipProps,
    ...tooltipFromProps,
  };
  const containerRef = useRef(null);
  const itemRefs = useRef([]);
  const [showTooltip, setShowTooltip] = useState(false);
  const [visibleTags, setVisibleTags] = useState([]);
  const [hiddenTags, setHiddenTags] = useState([]);

  const calculateVisibleTags = () => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth || 0;
      let totalWidth = 0;
      const visible = [];
      const hidden = [];

      tags.forEach((tag, index) => {
        const itemWidth = itemRefs.current[index]?.offsetWidth || itemWidthFromProps;
        if (typeof maxCount === 'number') {
          if (visible.length < maxCount) {
            visible.push(tag);
          } else {
            hidden.push(tag);
          }
        } else {
          if (totalWidth + itemWidth <= containerWidth) {
            visible.push(tag);
            totalWidth += itemWidth;
          } else {
            hidden.push(tag);
          }
        }
      });

      setVisibleTags(visible);
      setHiddenTags(hidden);
    }
  };

  useLayoutEffect(() => {
    if (asMuchAsPossible) {
      calculateVisibleTags();
    } else if (typeof maxCount === 'number') {
      const visible = tags.slice(0, maxCount);
      const hidden = tags.slice(maxCount);
      setVisibleTags(visible);
      setHiddenTags(hidden);
    } else {
      setVisibleTags(tags);
      setHiddenTags([]);
    }
  }, [tags, maxCount, asMuchAsPossible, itemWidthFromProps]);

  useEffect(() => {
    if (asMuchAsPossible) {
      const handleResize = throttle(() => {
        calculateVisibleTags();
      }, 200); // Adjust throttle interval as needed

      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
        handleResize.cancel(); // Cancel any pending throttled calls
      };
    }
  }, [asMuchAsPossible, tags]);

  const renderTooltipContent = () => {
    return (
      <Paragraph className={classes.tooltipContent}>
        {tags
          .map((tag) => (typeof tag.children === 'string' ? tag.children : null))
          .filter(Boolean)
          .join(', ')}
      </Paragraph>
    );
  };
  const tooltipContent =
    typeof renderTooltipContentFromProps === 'function'
      ? renderTooltipContentFromProps({
          visibleTags,
          hiddenTags,
          tags,
        })
      : renderTooltipContent();

  const renderVisibleTags = () => {
    return (
      <ul className={cn(classes.tagGroup, classes[direction], { [classes.wrap]: wrap })}>
        {visibleTags.map((tag, index) => (
          <li className={classes.listItem} key={index}>
            <Tag {...tag} />
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div ref={containerRef} className={classes.content}>
      {asMuchAsPossible && (
        <ul
          className={cn(classes.tagGroup, classes.shadow, classes[direction], {
            [classes.wrap]: wrap,
          })}>
          {tags.map((tag, index) => (
            <li
              className={classes.listItem}
              key={index}
              ref={(el) => (itemRefs.current[index] = el)}>
              <Tag {...tag} />
            </li>
          ))}
        </ul>
      )}
      <Tooltip
        color="#3A4861"
        overlayClassName={classes.tooltip}
        title={tooltipContent}
        open={showTooltip}
        onOpenChange={(open) => {
          if (asMuchAsPossible && !!hiddenTags.length) {
            setShowTooltip(open);
          }
        }}
        {...tooltipProps}>
        {renderVisibleTags()}
      </Tooltip>
    </div>
  );
};

TagGroup.defaultProps = {
  tags: [],
  maxCount: undefined,
  direction: 'horizontal',
  wrap: false,
  asMuchAsPossible: false,
  itemWidthFromProps: 50,
};
